/**
 * De periodekeuze die de prijshistorie en de prijsgrafiek delen (US-023).
 *
 * Dit stond twee keer uitgeschreven, en dat was precies één keer te veel: de grafiek had "7 dagen"
 * als extra keuze die de historie niet kende. Wie tussen de twee schermen sprong, kwam dan stil op
 * een ander bereik uit dan hij had gekozen. Eén lijst maakt dat onmogelijk.
 *
 * De sleutels staan in het adres van de pagina, dus ze zijn niet vrij te wijzigen: een bestaande
 * bladwijzer of een gedeelde link moet blijven werken. Komt er een keuze bij, geef die dan een nieuwe
 * sleutel en laat de bestaande staan.
 */

import { addDays, toLocalDateKey } from '@prijsmonitor/shared';

export type Periode = '30' | 'deze-maand' | 'vorige-maand' | '90' | 'jaar' | 'custom';

/** De knoppen, in de volgorde waarin ze op het scherm staan: kort naar lang. */
export const PERIODES: Array<{ key: Periode; label: string }> = [
  { key: '30', label: '30 dagen' },
  { key: 'deze-maand', label: 'Deze maand' },
  { key: 'vorige-maand', label: 'Vorige maand' },
  { key: '90', label: '90 dagen' },
  { key: 'jaar', label: '1 jaar' },
];

/**
 * Zet een periodekeuze om naar een concreet datumbereik.
 *
 * `null` bij `custom` en bij een sleutel die niet (meer) bestaat; het scherm valt dan terug op de
 * datums uit het adres. Dat is met opzet geen fout: een oude bladwijzer met een verdwenen sleutel
 * hoort een bruikbaar scherm te geven, niet een melding.
 */
export function bereikVoor(periode: Periode): { van: string; tot: string } | null {
  const vandaag = toLocalDateKey();
  const [jaar, maand] = vandaag.split('-').map(Number);

  switch (periode) {
    case '30':
      return { van: addDays(vandaag, -29), tot: vandaag };
    case '90':
      return { van: addDays(vandaag, -89), tot: vandaag };
    case 'jaar':
      // 365 dagen tot en met vandaag, niet "hetzelfde dagnummer vorig jaar": dan valt er in een
      // schrikkeljaar een dag weg of bij.
      return { van: addDays(vandaag, -364), tot: vandaag };
    case 'deze-maand':
      return { van: `${jaar}-${String(maand).padStart(2, '0')}-01`, tot: vandaag };
    case 'vorige-maand': {
      const vorigeMaand = maand === 1 ? 12 : (maand as number) - 1;
      const vorigJaar = maand === 1 ? (jaar as number) - 1 : jaar;
      const eersteVanDezeMaand = `${jaar}-${String(maand).padStart(2, '0')}-01`;
      return {
        van: `${vorigJaar}-${String(vorigeMaand).padStart(2, '0')}-01`,
        tot: addDays(eersteVanDezeMaand, -1),
      };
    }
    default:
      return null;
  }
}
